import { useRef, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Document } from 'react-pdf'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { SignerStampsDocument } from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import DroppablePdfPage from 'modules/documents/components/EsignaturePlacementFile/DroppablePdfPage'
import FilesBrowser from 'modules/documents/components/EsignaturePlacementFile/FilesBrowser'
import SignersSidebar from 'modules/documents/components/EsignaturePlacementFile/SignersSidebar'
import {
  DEFAULT_HEIGHT_SIGNER_STAMP,
  DEFAULT_WIDTH_SIGNER_STAMP,
  Stamp,
} from 'modules/documents/components/EsignaturePlacementFile/constants'
import {
  PageSize,
  getPdfFileSizes,
  getPdfPages,
} from 'modules/documents/components/PdfViewer/pdfUtils'

interface Props {
  file: File
  fileId: string
  signerIds: string[]
  onChangeStamps: (signatureStamps: Stamp[]) => void
  goToPreviousFile: () => void
  goToNextFile: () => void
  currentFileIndex: number
  totalFiles: number
  pendingStampsToPlace: boolean[]
  showValidations: boolean
  sequentialSigning?: boolean
}

const EsignaturePlacementFile = ({
  file,
  fileId,
  signerIds,
  onChangeStamps,
  goToPreviousFile,
  goToNextFile,
  currentFileIndex,
  totalFiles,
  pendingStampsToPlace,
  showValidations,
  sequentialSigning,
}: Props) => {
  const [url, setUrl] = useState<string | null>(null)
  const parentRef = useRef<HTMLDivElement>(null)
  const { loading: loadingSigners, data } = useQuery(SignerStampsDocument, {
    variables: { signerIds },
  })
  const [widgets, setWidgets] = useState<Stamp[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [numPages, setNumPages] = useState<number>(0)
  const [pageSizes, setPageSizes] = useState<PageSize[]>([])

  const signers = data?.apiCore.accessesConnection.nodes || []

  useUnsafeEffect(() => {
    const asyncFilePages = async () => {
      const pages = await getPdfPages(file)
      const sizes = await getPdfFileSizes(file, pages)

      setNumPages(pages)
      setPageSizes(sizes)
      setLoading(false)
    }

    setLoading(true)
    setUrl(URL.createObjectURL(file))
    asyncFilePages()
  }, [file])

  const findExistingSignerStampSize = (
    signerId: string,
    targetPage: number,
    targetScale: number
  ): { width: number; height: number } | null => {
    const signerStamps = widgets.filter((w) => w.signer.id === signerId)
    if (signerStamps.length === 0) return null

    // Search same page first, then previous pages in descending order
    for (let p = targetPage; p >= 1; p--) {
      const stampsOnPage = signerStamps.filter((s) => s.page.number === p)
      if (stampsOnPage.length > 0) {
        // Take the last stamp placed on this page (most recent)
        const existing = stampsOnPage.at(-1)
        if (!existing) continue

        // Convert the existing stamp's pixel size to unscaled values,
        // then re-apply the target page's scale to handle pages with different dimensions
        const existingOriginalWidth = pageSizes[existing.page.number - 1]?.width ?? 0
        const existingScale =
          existingOriginalWidth > 0 ? existing.page.width / existingOriginalWidth : 1

        const unscaledWidth = existing.width / existingScale
        const unscaledHeight = existing.height / existingScale

        return {
          width: unscaledWidth * targetScale,
          height: unscaledHeight * targetScale,
        }
      }
    }

    return null
  }

  const onDrop = (signerId: string, page: number, x: number, y: number, scale: number) => {
    const signer = signers.find((s) => s.id === signerId)

    if (!signer) return

    const pageWidth = (pageSizes[page - 1]?.width ?? 0) * scale
    const pageHeight = (pageSizes[page - 1]?.height ?? 0) * scale
    const existingSize = findExistingSignerStampSize(signerId, page, scale)
    const stampWidth = existingSize?.width ?? DEFAULT_WIDTH_SIGNER_STAMP * scale
    const stampHeight = existingSize?.height ?? DEFAULT_HEIGHT_SIGNER_STAMP * scale
    const adjustedX = Math.min(x, pageWidth - stampWidth)
    const adjustedY = Math.min(y, pageHeight - stampHeight)

    const stamp = {
      id: uuid(),
      fileId,
      signer: {
        id: signer.id,
        fullName: signer.fullName,
        avatar: signer.avatar || '',
        jobTitle: signer.employee?.referenceContract?.jobTitle || '',
      },
      page: {
        number: page,
        width: pageWidth,
        height: pageHeight,
      },
      x: adjustedX,
      y: adjustedY,
      width: stampWidth,
      height: stampHeight,
    }

    setWidgets((stamps) => {
      const newStamps = [...stamps, stamp]

      onChangeStamps(newStamps)
      return newStamps
    })
  }

  const onDrag = (id: string, x: number, y: number) => {
    const currentStamp = widgets.find((widget) => widget.id === id)

    if (!currentStamp) return

    const adjustedX = Math.max(0, Math.min(x, currentStamp.page.width - currentStamp.width))
    const adjustedY = Math.max(0, Math.min(y, currentStamp.page.height - currentStamp.height))

    setWidgets((stamps) => {
      const draggedStamps = stamps.map((stamp) =>
        stamp.id === id ? { ...stamp, x: adjustedX, y: adjustedY } : stamp
      )

      onChangeStamps(draggedStamps)
      return draggedStamps
    })
  }

  const onResize = (id: string, width: number, height: number) => {
    setWidgets((stamps) => {
      const resizedStamps = stamps.map((stamp) =>
        stamp.id === id ? { ...stamp, width, height } : stamp
      )

      onChangeStamps(resizedStamps)
      return resizedStamps
    })
  }

  const onRemove = (id: string) => {
    setWidgets((stamps) => {
      const removedStamps = stamps.filter((item) => item.id !== id)

      onChangeStamps(removedStamps)
      return removedStamps
    })
  }

  if (loading || loadingSigners) {
    return (
      <Box
        width="full"
        height="full"
        alignItems="center"
        justifyContent="center"
        border={{ all: { width: 's2', style: 'solid', color: 'grey200' } }}
      >
        <Spinner />
      </Box>
    )
  }

  return (
    <>
      <Box
        flexDirection="row"
        height="full"
        border={{ all: { width: 's2', style: 'solid', color: 'grey200' } }}
      >
        <DndProvider backend={HTML5Backend}>
          <Box flexGrow background="grey800" alignItems="center" overflowY="auto" ref={parentRef}>
            <Document file={url} loading={<Spinner />}>
              {Array.from(new Array(numPages), (_el, number) => (
                <DroppablePdfPage
                  key={number}
                  stamps={widgets.filter(
                    (widget) => widget.fileId === fileId && widget.page.number === number + 1
                  )}
                  pageNumber={number + 1}
                  pageWidth={pageSizes[number]?.width || 0}
                  pageHeight={pageSizes[number]?.height || 0}
                  onDrop={onDrop}
                  onDrag={onDrag}
                  onResize={onResize}
                  onRemove={onRemove}
                  parentRef={parentRef}
                  signerIds={signerIds}
                  sequentialSigning={sequentialSigning}
                />
              ))}
            </Document>
          </Box>
          <Box
            flexDirection="column"
            height="full"
            border={{ left: { width: 's1', style: 'solid', color: 'grey200' } }}
          >
            {totalFiles > 1 && (
              <FilesBrowser
                currentFileIndex={currentFileIndex}
                totalFiles={totalFiles}
                pendingStampsToPlace={pendingStampsToPlace}
                showValidations={showValidations}
                onClickPrev={goToPreviousFile}
                onClickNext={goToNextFile}
              />
            )}
            <Box flexGrow overflowY="auto" style={{ minHeight: '0' }}>
              <SignersSidebar
                signers={signers}
                signerIds={signerIds}
                showValidations={showValidations}
                fileStamps={widgets.filter((widget) => widget.fileId === fileId)}
                sequentialSigning={sequentialSigning}
              />
            </Box>
          </Box>
        </DndProvider>
      </Box>
    </>
  )
}

export default EsignaturePlacementFile
